import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../adapters/powersync';
import type { BookDrawerPrefill } from './BookAppointmentDrawer';
import {
  qAppointmentsByDateRange,
  qAppointmentsByPatient,
  qAppointmentProviders,
  qAppointmentServicesWithCategory,
  qAppointmentStatuses,
  qAppointmentCategories,
  qPatientsList,
  type AppointmentRow,
  type AppointmentProviderRow,
  type AppointmentServiceWithCategoryRow,
  type AppointmentStatusRow,
  type AppointmentCategoryRow,
  type PatientListRow,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import type { ColorAppointmentsBy } from './useAppointmentCalendarSettings';

// ─── Enriched appointment for rendering ───

export interface EnrichedAppointment {
  id: string;
  patientId: string;
  patientName: string;
  providerId: string;
  providerName: string;
  serviceId: string;
  serviceName: string;
  categoryId: string;
  categoryName: string;
  statusId: string;
  statusLabel: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  durationMinutes: number;
  notes: string;
  color: string; // resolved pastel bg based on colorBy setting
  accentColor: string; // darker accent for left border
}

/** Convert an enriched appointment to a BookDrawerPrefill for edit mode */
export function appointmentToPrefill(appt: EnrichedAppointment): BookDrawerPrefill {
  return {
    appointmentId: appt.id,
    date: appt.date,
    startTime: appt.startTime,
    patientId: appt.patientId,
    providerId: appt.providerId,
    serviceId: appt.serviceId,
    statusId: appt.statusId,
    notes: appt.notes,
  };
}

// ─── Lookup maps ───

export interface LookupMaps {
  providers: Map<string, AppointmentProviderRow>;
  services: Map<string, AppointmentServiceWithCategoryRow>;
  statuses: Map<string, AppointmentStatusRow>;
  categories: Map<string, AppointmentCategoryRow>;
  patients: Map<string, PatientListRow>;
  providerList: AppointmentProviderRow[];
  serviceList: AppointmentServiceWithCategoryRow[];
  statusList: AppointmentStatusRow[];
  categoryList: AppointmentCategoryRow[];
}

// ─── Color helpers ───

const FALLBACK_COLORS = [
  '#dbeafe',
  '#fef3c7',
  '#d1fae5',
  '#fce7f3',
  '#e0e7ff',
  '#f3e8ff',
  '#fef9c3',
  '#ccfbf1',
];

const ACCENT_MAP: Record<string, string> = {
  '#dbeafe': '#93c5fd',
  '#fef3c7': '#fbbf24',
  '#d1fae5': '#6ee7b7',
  '#fce7f3': '#f9a8d4',
  '#e0e7ff': '#a5b4fc',
  '#f3e8ff': '#c4b5fd',
  '#fef9c3': '#facc15',
  '#ccfbf1': '#5eead4',
};

function hexToPasstel(hex: string | null): string {
  if (!hex) return FALLBACK_COLORS[0];
  // If already a pastel-ish color, return as-is
  return hex;
}

function accentFor(color: string): string {
  return ACCENT_MAP[color] ?? darkenHex(color);
}

function darkenHex(hex: string): string {
  // Simple darken: reduce each channel by ~30%
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return '#94a3b8';
  const r = Math.max(0, Math.round(parseInt(m[1], 16) * 0.7));
  const g = Math.max(0, Math.round(parseInt(m[2], 16) * 0.7));
  const b = Math.max(0, Math.round(parseInt(m[3], 16) * 0.7));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function resolveColor(
  row: AppointmentRow,
  colorBy: ColorAppointmentsBy,
  lookups: LookupMaps,
): { color: string; accent: string } {
  let hex: string | null = null;

  if (colorBy === 'category') {
    const cat = lookups.categories.get(row.category_id ?? '');
    hex = cat?.color_hex ?? null;
  } else if (colorBy === 'provider') {
    const prov = lookups.providers.get(row.provider_id ?? '');
    hex = prov?.avatar_color ?? null;
  } else if (colorBy === 'status') {
    const st = lookups.statuses.get(row.status_id ?? '');
    hex = st?.color_hex ?? null;
  }

  if (!hex) {
    // Deterministic fallback based on appointment id
    const idx = Math.abs(hashCode(row.id)) % FALLBACK_COLORS.length;
    hex = FALLBACK_COLORS[idx];
  }

  const color = hexToPasstel(hex);
  return { color, accent: accentFor(color) };
}

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h;
}

// ─── Hook ───

export function useAppointmentsData(
  startDate: string,
  endDate: string,
  colorBy: ColorAppointmentsBy,
): { appointments: EnrichedAppointment[]; lookups: LookupMaps } {
  const { user } = useAuth();

  const { data: apptRows = [] } = usePowerSyncQuery<AppointmentRow>(
    qAppointmentsByDateRange(startDate, endDate),
  );
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: serviceRows = [] } = usePowerSyncQuery<AppointmentServiceWithCategoryRow>(
    qAppointmentServicesWithCategory(),
  );
  const { data: statusRows = [] } = usePowerSyncQuery<AppointmentStatusRow>(qAppointmentStatuses());
  const { data: categoryRows = [] } =
    usePowerSyncQuery<AppointmentCategoryRow>(qAppointmentCategories());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientListRow>(
    qPatientsList({ userId: user?.uid ?? '', sort: 'name-az' }),
  );

  const lookups = useMemo<LookupMaps>(() => {
    const providers = new Map(providerRows.map((r) => [r.id, r]));
    const services = new Map(serviceRows.map((r) => [r.id, r]));
    const statuses = new Map(statusRows.map((r) => [r.id, r]));
    const categories = new Map(categoryRows.map((r) => [r.id, r]));
    const patients = new Map(patientRows.map((r) => [r.id, r]));
    return {
      providers,
      services,
      statuses,
      categories,
      patients,
      providerList: providerRows.filter((r) => r.active == 1),
      serviceList: serviceRows.filter((r) => r.active == 1),
      statusList: statusRows.filter((r) => r.active == 1),
      categoryList: categoryRows.filter((r) => r.active == 1),
    };
  }, [providerRows, serviceRows, statusRows, categoryRows, patientRows]);

  const appointments = useMemo<EnrichedAppointment[]>(() => {
    return apptRows.map((row) => {
      const patient = lookups.patients.get(row.patient_id ?? '');
      const provider = lookups.providers.get(row.provider_id ?? '');
      const service = lookups.services.get(row.service_id ?? '');
      const status = lookups.statuses.get(row.status_id ?? '');
      const category = lookups.categories.get(row.category_id ?? service?.category_id ?? '');
      const { color, accent } = resolveColor(row, colorBy, lookups);

      return {
        id: row.id,
        patientId: row.patient_id ?? '',
        patientName: patient?.full_name ?? 'Unknown Patient',
        providerId: row.provider_id ?? '',
        providerName: provider?.display_name || provider?.full_name || 'Unknown Provider',
        serviceId: row.service_id ?? '',
        serviceName: service?.name ?? '',
        categoryId: row.category_id ?? service?.category_id ?? '',
        categoryName: category?.name ?? service?.category_name ?? '',
        statusId: row.status_id ?? '',
        statusLabel: status?.label ?? '',
        date: row.date ?? '',
        startTime: row.start_time ?? '',
        durationMinutes: row.duration_minutes ?? 30,
        notes: row.notes ?? '',
        color,
        accentColor: accent,
      };
    });
  }, [apptRows, lookups, colorBy]);

  return { appointments, lookups };
}

// ─── Shared enrichment helper ───

function enrichRows(
  apptRows: AppointmentRow[],
  lookups: LookupMaps,
  colorBy: ColorAppointmentsBy,
): EnrichedAppointment[] {
  return apptRows.map((row) => {
    const patient = lookups.patients.get(row.patient_id ?? '');
    const provider = lookups.providers.get(row.provider_id ?? '');
    const service = lookups.services.get(row.service_id ?? '');
    const status = lookups.statuses.get(row.status_id ?? '');
    const category = lookups.categories.get(row.category_id ?? service?.category_id ?? '');
    const { color, accent } = resolveColor(row, colorBy, lookups);

    return {
      id: row.id,
      patientId: row.patient_id ?? '',
      patientName: patient?.full_name ?? 'Unknown Patient',
      providerId: row.provider_id ?? '',
      providerName: provider?.display_name || provider?.full_name || 'Unknown Provider',
      serviceId: row.service_id ?? '',
      serviceName: service?.name ?? '',
      categoryId: row.category_id ?? service?.category_id ?? '',
      categoryName: category?.name ?? service?.category_name ?? '',
      statusId: row.status_id ?? '',
      statusLabel: status?.label ?? '',
      date: row.date ?? '',
      startTime: row.start_time ?? '',
      durationMinutes: row.duration_minutes ?? 30,
      notes: row.notes ?? '',
      color,
      accentColor: accent,
    };
  });
}

// ─── Patient-scoped hook (all appointments for a given patient, no date range) ───

export function usePatientAppointmentsData(
  patientId: string,
  colorBy: ColorAppointmentsBy,
): { appointments: EnrichedAppointment[]; lookups: LookupMaps } {
  const { user } = useAuth();

  const { data: apptRows = [] } = usePowerSyncQuery<AppointmentRow>(
    qAppointmentsByPatient(patientId),
  );
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: serviceRows = [] } = usePowerSyncQuery<AppointmentServiceWithCategoryRow>(
    qAppointmentServicesWithCategory(),
  );
  const { data: statusRows = [] } = usePowerSyncQuery<AppointmentStatusRow>(qAppointmentStatuses());
  const { data: categoryRows = [] } =
    usePowerSyncQuery<AppointmentCategoryRow>(qAppointmentCategories());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientListRow>(
    qPatientsList({ userId: user?.uid ?? '', sort: 'name-az' }),
  );

  const lookups = useMemo<LookupMaps>(() => {
    const providers = new Map(providerRows.map((r) => [r.id, r]));
    const services = new Map(serviceRows.map((r) => [r.id, r]));
    const statuses = new Map(statusRows.map((r) => [r.id, r]));
    const categories = new Map(categoryRows.map((r) => [r.id, r]));
    const patients = new Map(patientRows.map((r) => [r.id, r]));
    return {
      providers,
      services,
      statuses,
      categories,
      patients,
      providerList: providerRows.filter((r) => r.active == 1),
      serviceList: serviceRows.filter((r) => r.active == 1),
      statusList: statusRows.filter((r) => r.active == 1),
      categoryList: categoryRows.filter((r) => r.active == 1),
    };
  }, [providerRows, serviceRows, statusRows, categoryRows, patientRows]);

  const appointments = useMemo<EnrichedAppointment[]>(
    () => enrichRows(apptRows, lookups, colorBy),
    [apptRows, lookups, colorBy],
  );

  return { appointments, lookups };
}
